import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  InformationCircleIcon,
  AdjustmentsHorizontalIcon,
  SparklesIcon,
  CpuChipIcon,
  RocketLaunchIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Text } from "@core/text";
import { AdditionalArrangementsDto } from "@packages/api/madlenAPI.schemas";
interface AdditionalInstructionsStepProps {
  additionalInstructions: string;
  onAdditionalInstructionsChange: (value: string) => void;
  additionalArrangements: AdditionalArrangementsDto;
  onArrangementsChange: (arrangements: AdditionalArrangementsDto) => void;
}

const AdditionalInstructionsStep: React.FC<AdditionalInstructionsStepProps> = ({
  additionalInstructions,
  onAdditionalInstructionsChange,
  additionalArrangements,
  onArrangementsChange,
}) => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState<{
    differentiation: boolean;
    funActivities: boolean;
    technology: boolean;
    ai: boolean;
  }>({
    differentiation: false,
    funActivities: false,
    technology: false,
    ai: false,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div className="space-y-8">
      {/* Additional Arrangements Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <Text variant="h3" className="mb-6">
            {t("dailyLessonPlan.additional.arrangements.title")}
          </Text>
          <div className="space-y-4">
            {/* Differentiation */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection("differentiation")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <AdjustmentsHorizontalIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {t(
                        "dailyLessonPlan.additional.arrangements.differentiation.title",
                      )}
                    </h4>
                    <Text variant="p" status="secondary">
                      dailyLessonPlan.additional.arrangements.differentiation.description
                    </Text>
                  </div>
                </div>
                {openSections.differentiation ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                )}
              </button>
              <AnimatePresence>
                {openSections.differentiation && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <textarea
                        className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                        placeholder={t(
                          "dailyLessonPlan.additional.arrangements.differentiation.placeholder",
                        )}
                        value={
                          additionalArrangements.differentiation?.join("\n") ||
                          ""
                        }
                        onChange={(e) =>
                          onArrangementsChange({
                            ...additionalArrangements,
                            differentiation: e.target.value
                              .split("\n")
                              .filter(Boolean),
                          })
                        }
                        rows={3}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Fun Activities */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection("funActivities")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <SparklesIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {t(
                        "dailyLessonPlan.additional.arrangements.funActivities.title",
                      )}
                    </h4>
                    <Text variant="p" status="secondary">
                      dailyLessonPlan.additional.arrangements.funActivities.description
                    </Text>
                  </div>
                </div>
                {openSections.funActivities ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                )}
              </button>
              <AnimatePresence>
                {openSections.funActivities && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <textarea
                        className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                        placeholder={t(
                          "dailyLessonPlan.additional.arrangements.funActivities.placeholder",
                        )}
                        value={
                          additionalArrangements.funActivities?.join("\n") || ""
                        }
                        onChange={(e) =>
                          onArrangementsChange({
                            ...additionalArrangements,
                            funActivities: e.target.value
                              .split("\n")
                              .filter(Boolean),
                          })
                        }
                        rows={3}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Technology */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection("technology")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <CpuChipIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {t(
                        "dailyLessonPlan.additional.arrangements.technology.title",
                      )}
                    </h4>
                    <Text variant="p" status="secondary">
                      dailyLessonPlan.additional.arrangements.technology.description
                    </Text>
                  </div>
                </div>
                {openSections.technology ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                )}
              </button>
              <AnimatePresence>
                {openSections.technology && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <textarea
                        className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                        placeholder={t(
                          "dailyLessonPlan.additional.arrangements.technology.placeholder",
                        )}
                        value={
                          additionalArrangements.technology?.join("\n") || ""
                        }
                        onChange={(e) =>
                          onArrangementsChange({
                            ...additionalArrangements,
                            technology: e.target.value
                              .split("\n")
                              .filter(Boolean),
                          })
                        }
                        rows={3}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* AI Usage */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection("ai")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <RocketLaunchIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {t("dailyLessonPlan.additional.arrangements.ai.title")}
                    </h4>
                    <Text variant="p" status="secondary">
                      dailyLessonPlan.additional.arrangements.ai.description
                    </Text>
                  </div>
                </div>
                {openSections.ai ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                )}
              </button>
              <AnimatePresence>
                {openSections.ai && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <textarea
                        className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                        placeholder={t(
                          "dailyLessonPlan.additional.arrangements.ai.placeholder",
                        )}
                        value={additionalArrangements.ai?.join("\n") || ""}
                        onChange={(e) =>
                          onArrangementsChange({
                            ...additionalArrangements,
                            ai: e.target.value.split("\n").filter(Boolean),
                          })
                        }
                        rows={3}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Additional Instructions Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <Text variant="h3" className="mb-4">
            {t("dailyLessonPlan.additional.instructions.title")}
          </Text>
          <textarea
            value={additionalInstructions}
            onChange={(e) => onAdditionalInstructionsChange(e.target.value)}
            className="w-full p-4 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
            rows={4}
            placeholder={t(
              "dailyLessonPlan.additional.instructions.placeholder",
            )}
          />
        </div>
      </motion.div>

      {/* Helper Text */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-emerald-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
              {t("dailyLessonPlan.additional.helper.title")}
            </h3>
            <Text variant="p" status="success" size="sm" className="mt-2">
              dailyLessonPlan.additional.helper.description
            </Text>
            <ul className="mt-3 list-disc list-inside text-sm text-emerald-700 dark:text-emerald-300">
              <li>{t("dailyLessonPlan.additional.helper.tip1")}</li>
              <li>{t("dailyLessonPlan.additional.helper.tip2")}</li>
              <li>{t("dailyLessonPlan.additional.helper.tip3")}</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdditionalInstructionsStep;
